import { format } from 'number-currency-format';
import get from 'lodash/get';
import { getCurrencySign } from './currency.js';
import { a as LanguageCode } from '../language-BHHwgtQp.js';
import { C as CurrencyCode } from '../currency-_kacFJHO.js';

var CurrencyPosition;
(function (CurrencyPosition) {
    CurrencyPosition["Left"] = "LEFT";
    CurrencyPosition["Right"] = "RIGHT";
})(CurrencyPosition || (CurrencyPosition = {}));
var DecimalSeparator;
(function (DecimalSeparator) {
    DecimalSeparator["Point"] = ".";
    DecimalSeparator["Comma"] = ",";
})(DecimalSeparator || (DecimalSeparator = {}));
var ThousandSeparator;
(function (ThousandSeparator) {
    ThousandSeparator["Space"] = " ";
    ThousandSeparator["Dot"] = ".";
    ThousandSeparator["Point"] = ",";
})(ThousandSeparator || (ThousandSeparator = {}));
const getThousandSeparator = (language) => {
    if (language === LanguageCode.En)
        return ThousandSeparator.Point;
    if (language === LanguageCode.Fr)
        return ThousandSeparator.Space;
    return ThousandSeparator.Dot;
};
const formatPrice = (price, currencyCode, language, noZeroAfterDecimal = false, isDiscount) => {
    if (typeof price !== 'number')
        return '';
    const currencyPosition = language === LanguageCode.En ? CurrencyPosition.Left : CurrencyPosition.Right;
    const decimalSeparator = language === LanguageCode.En ? DecimalSeparator.Point : DecimalSeparator.Comma;
    const thousandSeparator = getThousandSeparator(language);
    const spacing = language !== LanguageCode.En ||
        currencyCode === CurrencyCode.AED ||
        currencyCode === CurrencyCode.CHF;
    const currencySign = getCurrencySign(currencyCode);
    const formatDiscount = (priceText) => {
        const showMinusSign = isDiscount && (language === LanguageCode.Es || language === LanguageCode.Fr);
        if (showMinusSign)
            return `-${priceText}`;
        return priceText;
    };
    let formattedPrice = format(price, {
        currency: currencySign,
        spacing,
        currencyPosition,
        decimalSeparator,
        thousandSeparator,
    });
    // Remove ".00" or ",00" for all currencies
    if (noZeroAfterDecimal) {
        if (language === LanguageCode.En) {
            formattedPrice = formattedPrice.replace(/\.00$/, '');
        }
        else {
            formattedPrice = formattedPrice.replace(/,00/, '');
        }
    }
    return formatDiscount(formattedPrice);
};
const getPrices = (priceRange, language, noZeroAfterDecimal = false) => {
    const oldMinPrice = get(priceRange, ['minimum_price', 'regular_price']);
    const oldMaxPrice = get(priceRange, ['maximum_price', 'regular_price']);
    const newMinPrice = get(priceRange, ['minimum_price', 'final_price']);
    const newMaxPrice = get(priceRange, ['maximum_price', 'final_price']);
    const oldMinPriceVal = oldMinPrice === null || oldMinPrice === void 0 ? void 0 : oldMinPrice.value;
    const oldMaxPriceVal = oldMaxPrice === null || oldMaxPrice === void 0 ? void 0 : oldMaxPrice.value;
    const newMinPriceVal = newMinPrice === null || newMinPrice === void 0 ? void 0 : newMinPrice.value;
    const newMaxPriceVal = newMaxPrice === null || newMaxPrice === void 0 ? void 0 : newMaxPrice.value;
    const oldMinPriceCurrency = oldMinPrice === null || oldMinPrice === void 0 ? void 0 : oldMinPrice.currency;
    const oldMaxPriceCurrency = oldMaxPrice === null || oldMaxPrice === void 0 ? void 0 : oldMaxPrice.currency;
    const newMinPriceCurrency = newMinPrice === null || newMinPrice === void 0 ? void 0 : newMinPrice.currency;
    const newMaxPriceCurrency = newMaxPrice === null || newMaxPrice === void 0 ? void 0 : newMaxPrice.currency;
    const currencyCode = oldMinPriceCurrency ||
        oldMaxPriceCurrency ||
        newMinPriceCurrency ||
        newMaxPriceCurrency;
    const price = {
        oldPrice: '',
        newPrice: '',
    };
    if ([oldMinPriceVal, oldMaxPriceVal, newMinPriceVal].every(priceValue => priceValue === newMaxPriceVal)) {
        price.newPrice = formatPrice(newMaxPriceVal, currencyCode, language, noZeroAfterDecimal);
        return price;
    }
    if (oldMinPriceVal === oldMaxPriceVal) {
        price.oldPrice = formatPrice(oldMaxPriceVal, currencyCode, language, noZeroAfterDecimal);
    }
    else {
        price.oldPrice = `${formatPrice(oldMinPriceVal, currencyCode, language, noZeroAfterDecimal)} - ${formatPrice(oldMaxPriceVal, currencyCode, language, noZeroAfterDecimal)}`;
    }
    if (newMinPriceVal === newMaxPriceVal) {
        price.newPrice = formatPrice(newMaxPriceVal, currencyCode, language, noZeroAfterDecimal);
    }
    else {
        price.newPrice = `${formatPrice(newMinPriceVal, currencyCode, language, noZeroAfterDecimal)} - ${formatPrice(newMaxPriceVal, currencyCode, language, noZeroAfterDecimal)}`;
    }
    return price;
};
const isPriceZero = (priceWithCurrency) => {
    const priceValue = parseFloat(priceWithCurrency.replace(/[^\d.-]/g, ''));
    return priceValue === 0;
};
const formatPoints = (value, language) => {
    if (value === null || value === undefined || value === '')
        return '';
    // Normalise into a pure number first
    const numeric = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
    if (Number.isNaN(numeric))
        return '';
    const thousandSeparator = getThousandSeparator(language);
    // Insert the separator every three digits from the right
    return numeric.toString().replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
};

export { formatPoints, formatPrice, getPrices, isPriceZero };
//# sourceMappingURL=price.js.map
